import { useEffect, useState, useCallback } from 'react';
import api from '../api/axios';

interface CustomFieldStats {
    subscriptionPreferences: number;
    emailLogs: number;
    websiteInteractions: number;
}


interface SyncReport {
    _id: string;
    organizationId: string;
    organizationName: string;
    startTime: string;
    endTime?: string;
    status: 'running' | 'completed' | 'failed' | 'partial';
    hubspotContactsFetched: number;
    hubspotCompaniesFetched: number;
    hubspotTotalRecords: number;
    hubspotErrors: number;
    blackbaudCustomFieldsUpdated: number;
    blackbaudTotalUpdates: number;
    customFieldStats?: CustomFieldStats;
    totalProcessed: number;
    errorDetails?: string[];
}

interface CurrentSyncStatus {
    runningSyncs: SyncReport[];
    recentActivity: SyncReport[];
    statistics: {
        totalSyncs: number;
        totalRecordsProcessed: number;
        totalCustomFieldsCreated: number;
        totalErrors: number;
        avgSuccessRate: number;
    };
}

export default function SyncReportsDashboard() {
    const [currentStatus, setCurrentStatus] = useState<CurrentSyncStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [selectedOrg, setSelectedOrg] = useState<string>('');
    const [triggerModalOpen, setTriggerModalOpen] = useState(false);
    const [triggeringSync, setTriggeringSync] = useState(false);

    const fetchData = useCallback(async (showRefreshing = false) => {
        try {
            if (showRefreshing) setRefreshing(true);
            else setLoading(true);

            const statusResponse = await api.get('/sync/status');
            setCurrentStatus((statusResponse.data as any).data);
        } catch (error) {
            console.error('Failed to fetch sync reports data:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
        const interval = setInterval(() => fetchData(true), 30000); // Refresh every 30s
        return () => clearInterval(interval);
    }, [fetchData]);

    const handleTriggerSync = async () => {
        if (!selectedOrg) return;
        
        setTriggeringSync(true);
        try {
            await api.post('/sync/trigger', {
                organizationId: selectedOrg
            });
            
            setTriggerModalOpen(false);
            setSelectedOrg('');
            setTimeout(() => fetchData(true), 2000); // Refresh after 2s
        } catch (error: any) {
            alert(error?.response?.data?.message || 'Failed to trigger sync');
        } finally {
            setTriggeringSync(false);
        }
    };

    const getStatusBadge = (status: string) => {
        const badgeMap = {
            running: 'badge bg-primary',
            processing: 'badge bg-primary',
            completed: 'badge bg-success',
            pending: 'badge bg-secondary',
            failed: 'badge bg-danger',
            partial: 'badge bg-warning text-dark'
        };
        return badgeMap[status as keyof typeof badgeMap] || 'badge bg-secondary';
    };


    const formatNumber = (num: number) => {
        return new Intl.NumberFormat().format(num);
    };

    if (loading) {
        return (
            <div className="container-fluid py-4">
                <div className="d-flex justify-content-center align-items-center" style={{minHeight: '400px'}}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container-fluid py-4">
            {/* Header */}
            <div className="row mb-4">
                <div className="col">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h1 className="h3 mb-1">Custom Field Sync Reports Dashboard</h1>
                            <p className="text-muted mb-0">Organization-wise HubSpot & Blackbaud sync monitoring</p>
                        </div>
                        <div>
                            <button 
                                className="btn btn-outline-primary me-2" 
                                onClick={() => fetchData(true)}
                                disabled={refreshing}
                            >
                                {refreshing ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-1" />
                                        Refreshing...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-arrow-clockwise me-1"></i>
                                        Refresh
                                    </>
                                )}
                            </button>
                            <button 
                                className="btn btn-primary" 
                                onClick={() => setTriggerModalOpen(true)}
                            >
                                <i className="bi bi-play-circle me-1"></i>
                                Trigger Sync
                            </button>
                        </div>
                    </div>
                </div>
            </div>


            {/* Running Syncs Alert */}
            {currentStatus?.runningSyncs && currentStatus.runningSyncs.length > 0 && (
                <div className="row mb-4">
                    <div className="col">
                        <div className="alert alert-info d-flex align-items-center">
                            <i className="bi bi-info-circle me-2"></i>
                            <strong>Currently Running:</strong>
                            <span className="ms-2">
                                {currentStatus.runningSyncs.map((sync, idx) => (
                                    <span key={sync._id} className="badge bg-primary me-1">
                                        {sync.organizationName}
                                        {idx < currentStatus.runningSyncs.length - 1 && ', '}
                                    </span>
                                ))}
                            </span>
                        </div>
                    </div>
                </div>
            )}


            {/* Organization Sync History */}
            {currentStatus?.recentActivity && currentStatus.recentActivity.length > 0 && (
                <div className="row mt-4">
                    <div className="col">
                        <div className="card">
                            <div className="card-header">
                                <h5 className="card-title mb-0">Organization Sync History</h5>
                            </div>
                            <div className="card-body p-0">
                                <div className="table-responsive">
                                    <table className="table table-sm mb-0">
                                        <thead className="table-light">
                                            <tr>
                                                <th>Time</th>
                                                <th>Organization</th>
                                                <th>Status</th>
                                                <th>Total Contacts</th>
                                                <th>Create (S/F)</th>
                                                <th>Update (S/F)</th>
                                                <th>Duration</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {currentStatus.recentActivity.slice(0, 10).map((activity) => {
                                                const start = new Date(activity.startTime);
                                                const end = activity.endTime ? new Date(activity.endTime) : null;
                                                const durationMs = end ? (end.getTime() - start.getTime()) : 0;
                                                const duration = end ? `${Math.floor(durationMs / 60000)}m ${Math.floor((durationMs % 60000)/1000)}s` : 'N/A';
                                                const statusLabel = activity.status.charAt(0).toUpperCase() + activity.status.slice(1);
                                                
                                                // Calculate create/update stats (placeholder for now since we don't have separate counts)
                                                const createSuccess = 0;
                                                const createFailed = 0;
                                                const updateSuccess = activity.blackbaudCustomFieldsUpdated || 0;
                                                const updateFailed = activity.hubspotErrors || 0;
                                                
                                                return (
                                                    <tr key={activity._id}>
                                                        <td>{start.toLocaleString()}</td>
                                                        <td><strong>{activity.organizationName}</strong></td>
                                                        <td><span className={getStatusBadge(activity.status)}>{statusLabel}</span></td>
                                                        <td>{formatNumber(activity.hubspotContactsFetched || 0)}</td>
                                                        <td>
                                                            <span className="text-success">{formatNumber(createSuccess)}</span>
                                                            <span>/</span>
                                                            <span className="text-danger">{formatNumber(createFailed)}</span>
                                                        </td>
                                                        <td>
                                                            <span className="text-success">{formatNumber(updateSuccess)}</span>
                                                            <span>/</span>
                                                            <span className="text-danger">{formatNumber(updateFailed)}</span>
                                                        </td>
                                                        <td>{duration}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Trigger Sync Modal */}
            {triggerModalOpen && (
                <div className="modal show d-block" tabIndex={-1} style={{backgroundColor: 'rgba(0,0,0,0.5)'}}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Trigger Manual Sync</h5>
                                <button 
                                    type="button" 
                                    className="btn-close" 
                                    onClick={() => {
                                        setTriggerModalOpen(false);
                                        setSelectedOrg('');
                                    }}
                                    disabled={triggeringSync}
                                ></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label htmlFor="orgSelect" className="form-label">Select Organization</label>
                                    <select 
                                        id="orgSelect"
                                        className="form-select" 
                                        value={selectedOrg} 
                                        onChange={(e) => setSelectedOrg(e.target.value)}
                                        disabled={triggeringSync}
                                    >
                                        <option value="">Choose organization...</option>
                                        {currentStatus?.recentActivity.map((activity) => (
                                            <option key={activity.organizationId} value={activity.organizationId}>
                                                {activity.organizationName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={() => {
                                        setTriggerModalOpen(false);
                                        setSelectedOrg('');
                                    }}
                                    disabled={triggeringSync}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="button" 
                                    className="btn btn-primary" 
                                    onClick={handleTriggerSync}
                                    disabled={!selectedOrg || triggeringSync}
                                >
                                    {triggeringSync ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-1" />
                                            Triggering...
                                        </>
                                    ) : (
                                        'Trigger Sync'
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}